import { ready as apiReady, buildApiUrl } from './api-host';
const COLLECT_URL = '/api/tracking/collect';
const SESSION_KEY = '__ms_sid';
const SESSION_TTL = 86400000; // 24ч
const FLUSH_INTERVAL = 2000;
const FLUSH_BATCH_SIZE = 10;
const MAX_BATCH = 50;
const LOCAL_DEV_VISIT_ID = 100001;
const LOCAL_DEV_OFFER_VISIT_START = 200000;
const ALLOWED_TYPES = [
    'showcase_view', 'offer_impression', 'showcase_scroll',
    'showcase_engagement', 'offer_click', 'offer_redirect', 'showcase_exit',
    'tab_active', 'tab_inactive',
];

let sessionId = null;
let visitId = null;
let serverTimeOffset = 0;
let serverTimeReady = false;
let queue = [];
let lastClickAt = null;
let lastOfferClickId = null;
let lastClickOfferIdOffer = null;
let lastClickOfferVisitId = null;
let lastClickTargetUrl = null;
let redirectSent = false;
let maxScrollPercent = 0;
let activeMs = 0;
let activeStart = null;
let flushTimer = null;
let impressedOffers = new Set();
let scrollMilestones = new Set();
let exitSent = false;
let trackerStarted = false;
let tabIsInactive = false;
let pageIsClosing = false;
let tabInactiveTimer = null;

function isLocalDevPreview() {
    return ['localhost', '127.0.0.1'].includes(location.hostname) && location.pathname.endsWith('/preview.html');
}

function getOrCreateSession() {
    try {
        const raw = localStorage.getItem(SESSION_KEY);
        if (raw) {
            const parsed = JSON.parse(raw);
            if (parsed.ts && Date.now() - parsed.ts < SESSION_TTL) {
                return parsed.id;
            }
        }
    } catch (_) {}
    const id = window.crypto && window.crypto.randomUUID ? window.crypto.randomUUID() : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
        const r = Math.random() * 16 | 0;
        return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
    });
    try {
        localStorage.setItem(SESSION_KEY, JSON.stringify({ id, ts: Date.now() }));
    } catch (_) {}
    return id;
}

function ensureLocalDevVisitContext() {
    if (!isLocalDevPreview()) return;

    if (window.__visitId === null || typeof window.__visitId === 'undefined') {
        window.__visitId = LOCAL_DEV_VISIT_ID;
    }

    visitId = window.__visitId;

    document.querySelectorAll('[data-offer-id]').forEach((el, index) => {
        if (!el.dataset.offerVisitId) {
            el.dataset.offerVisitId = String(LOCAL_DEV_OFFER_VISIT_START + index + 1);
        }
    });
}

function getOfferVisitId(el) {
    ensureLocalDevVisitContext();
    const offerVisitId = parseInt(el.dataset.offerVisitId, 10);
    return Number.isNaN(offerVisitId) ? null : offerVisitId;
}

function nowIso() {
    return new Date(Date.now() + serverTimeOffset).toISOString();
}

function push(type, payload, meta = {}) {
    if (window.__workOffline === true) return;
    if (!serverTimeReady) return;
    if (!ALLOWED_TYPES.includes(type)) return;
    queue.push({ type, occurred_at: nowIso(), ...meta, payload });
    if (queue.length >= FLUSH_BATCH_SIZE) flush();
}

function pushShowcaseView(extraPayload = {}) {
    push('showcase_view', {
        url: location.href,
        referrer: document.referrer,
        utm_source: new URLSearchParams(location.search).get('utm_source'),
        utm_medium: new URLSearchParams(location.search).get('utm_medium'),
        utm_campaign: new URLSearchParams(location.search).get('utm_campaign'),
        utm_content: new URLSearchParams(location.search).get('utm_content'),
        utm_term: new URLSearchParams(location.search).get('utm_term'),
        user_agent: navigator.userAgent,
        ...extraPayload,
    });
}

function flush(beacon) {
    if (window.__workOffline === true) return;
    if (queue.length === 0) return;
    const batch = queue.splice(0, MAX_BATCH);
    const body = JSON.stringify({ session_id: sessionId, visit_id: visitId, events: batch });
    const collect = buildApiUrl(COLLECT_URL) || COLLECT_URL;
    if (beacon && navigator.sendBeacon) {
        navigator.sendBeacon(collect, new Blob([body], { type: 'application/json' }));
    } else {
        fetch(collect, {
            method: 'POST',
            keepalive: true,
            headers: { 'Content-Type': 'application/json' },
            body,
        }).catch(() => {});
    }
}

function flushBeacon() {
    flush(true);
}

function ensureFlushTimer() {
    if (!flushTimer) {
        flushTimer = setInterval(() => flush(false), FLUSH_INTERVAL);
    }
}

function startActiveTimer() {
    if (!activeStart) activeStart = Date.now();
}

function stopActiveTimer() {
    if (activeStart) {
        activeMs += Date.now() - activeStart;
        activeStart = null;
    }
}

function getPageScrollPercent() {
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
    const total = Math.max(
        document.documentElement.scrollHeight || 0,
        document.body ? document.body.scrollHeight || 0 : 0
    );

    if (total <= 0) return 0;

    const viewedBottom = window.scrollY + viewportHeight;
    const maxScrollTop = Math.max(0, total - viewportHeight);

    if (window.scrollY >= maxScrollTop - 2 || viewedBottom >= total - 2) {
        return 100;
    }

    return Math.max(0, Math.min(100, Math.round((viewedBottom / total) * 100)));
}

function initScrollTracking() {
    let scrollRaf = null;

    function checkScrollDepth() {
        scrollRaf = null;
        const pct = getPageScrollPercent();
        maxScrollPercent = Math.max(maxScrollPercent, pct);

        [25, 50, 75, 100].forEach(milestone => {
            if (pct >= milestone && !scrollMilestones.has(milestone)) {
                scrollMilestones.add(milestone);
                push('showcase_scroll', { depth_percent: milestone });
            }
        });
    }

    function scheduleScrollDepthCheck() {
        if (scrollRaf !== null) return;
        scrollRaf = window.requestAnimationFrame(checkScrollDepth);
    }

    window.addEventListener('scroll', scheduleScrollDepthCheck, { passive: true });
    window.addEventListener('resize', scheduleScrollDepthCheck, { passive: true });
    window.addEventListener('orientationchange', scheduleScrollDepthCheck, { passive: true });
    window.addEventListener('load', scheduleScrollDepthCheck, { passive: true });

    if ('ResizeObserver' in window && document.body) {
        new ResizeObserver(scheduleScrollDepthCheck).observe(document.body);
    }

    scheduleScrollDepthCheck();
}

function initImpressionTracking() {
    const timers = new Map();
    const observed = new WeakSet();
    const observer = 'IntersectionObserver' in window
        ? new IntersectionObserver(entries => {
            entries.forEach(entry => handleOfferVisibility(entry.target));
        }, { threshold: [0, 0.5, 1] })
        : null;

    function getVisibleRatio(el) {
        const rect = el.getBoundingClientRect();
        if (rect.width <= 0 || rect.height <= 0) return 0;

        const visibleTop = Math.max(rect.top, 0);
        const visibleBottom = Math.min(rect.bottom, window.innerHeight || document.documentElement.clientHeight || 0);
        const visibleHeight = Math.max(0, visibleBottom - visibleTop);

        return visibleHeight / rect.height;
    }

    function registerImpression(el) {
        const offerId = el.dataset.offerId;
        if (!offerId || impressedOffers.has(offerId)) return;

        impressedOffers.add(offerId);
        const siblings = Array.from(document.querySelectorAll('[data-offer-id]'));
        const position = siblings.indexOf(el) + 1;
        push(
            'offer_impression',
            { offer_id: parseInt(offerId, 10), position },
            { offer_visit_id: getOfferVisitId(el) }
        );
    }

    function handleOfferVisibility(el) {
        const offerId = el?.dataset?.offerId;
        if (!offerId || impressedOffers.has(offerId)) return;

        if (getVisibleRatio(el) >= 0.5) {
            if (!timers.has(offerId)) {
                timers.set(offerId, setTimeout(() => {
                    if (getVisibleRatio(el) >= 0.5) {
                        registerImpression(el);
                    }
                    timers.delete(offerId);
                }, 1000));
            }
        } else {
            const t = timers.get(offerId);
            if (t) {
                clearTimeout(t);
                timers.delete(offerId);
            }
        }
    }

    function checkAllOffersVisibility() {
        document.querySelectorAll('[data-offer-id]').forEach(handleOfferVisibility);
    }

    function scheduleVisibilityCheck() {
        window.requestAnimationFrame(checkAllOffersVisibility);
    }

    function observeOffer(el) {
        if (observed.has(el)) return;
        observed.add(el);
        observer?.observe(el);
        handleOfferVisibility(el);
    }

    document.querySelectorAll('[data-offer-id]').forEach(observeOffer);

    window.addEventListener('scroll', scheduleVisibilityCheck, { passive: true });
    window.addEventListener('resize', scheduleVisibilityCheck, { passive: true });
    window.addEventListener('orientationchange', scheduleVisibilityCheck, { passive: true });
    document.addEventListener('visibilitychange', () => {
        if (!document.hidden) scheduleVisibilityCheck();
    });
    document.addEventListener('offers:updated', scheduleVisibilityCheck);

    if ('MutationObserver' in window) {
        new MutationObserver(mutations => {
            ensureLocalDevVisitContext();
            mutations.forEach(mutation => {
                mutation.addedNodes.forEach(node => {
                    if (!(node instanceof Element)) return;
                    if (node.matches('[data-offer-id]')) observeOffer(node);
                    node.querySelectorAll('[data-offer-id]').forEach(observeOffer);
                });
            });
            scheduleVisibilityCheck();
        }).observe(document.body, { childList: true, subtree: true });
    }
}

function initClickTracking() {
    document.addEventListener('click', e => {
        const el = e.target.closest('[data-offer-id]');
        if (!el) return;
        const offerId = parseInt(el.dataset.offerId, 10);
        const siblings = Array.from(document.querySelectorAll('[data-offer-id]'));
        const position = siblings.indexOf(el) + 1;
        lastClickAt = Date.now();
        lastClickOfferIdOffer = offerId;
        lastClickOfferVisitId = getOfferVisitId(el);
        redirectSent = false;
        // offer_click фиксируется backend-ом в /api/click/send, чтобы не было дубля из tracking collect.
        // Не обнуляем lastOfferClickId и lastClickTargetUrl - они устанавливаются через updateOfferClickId
    }, { capture: false, passive: true });
}

function initExitTracking() {
    function onExit() {
        if (exitSent) return;
        exitSent = true;
        pageIsClosing = true;
        if (tabInactiveTimer) {
            clearTimeout(tabInactiveTimer);
            tabInactiveTimer = null;
        }
        stopActiveTimer();
        const dwellMs = activeMs;
        push('showcase_engagement', { active_ms_since_load: activeMs });
        if (!redirectSent && lastClickAt && Date.now() - lastClickAt < 3000 && lastClickTargetUrl && lastOfferClickId && lastClickOfferIdOffer) {
            const redirectMeta = { offer_click_id: lastOfferClickId };
            if (lastClickOfferVisitId) {
                redirectMeta.offer_visit_id = lastClickOfferVisitId;
            }

            push('offer_redirect', {
                offer_id: lastClickOfferIdOffer,
                offer_click_id: lastOfferClickId,
                target_url: lastClickTargetUrl,
            }, redirectMeta);
            redirectSent = true;
        }
        push('showcase_exit', { dwell_ms: dwellMs, max_scroll_percent: maxScrollPercent });
        flushBeacon();
        if (flushTimer) {
            clearInterval(flushTimer);
            flushTimer = null;
        }
    }

    function onTabHidden() {
        if (pageIsClosing || tabIsInactive || tabInactiveTimer) return;
        stopActiveTimer();
        tabInactiveTimer = setTimeout(() => {
            tabInactiveTimer = null;
            if (pageIsClosing || !document.hidden || tabIsInactive) return;
            tabIsInactive = true;
            push('tab_inactive', {
                active_ms_since_load: activeMs,
                max_scroll_percent: maxScrollPercent,
            });
            flush();
        }, 150);
    }

    function onTabVisible() {
        if (tabInactiveTimer) {
            clearTimeout(tabInactiveTimer);
            tabInactiveTimer = null;
        }
        if (!tabIsInactive) return;
        tabIsInactive = false;
        pageIsClosing = false;
        startActiveTimer();
        push('tab_active', {
            url: location.href,
            referrer: document.referrer,
        });
        flush();
    }

    window.addEventListener('pagehide', onExit);
    window.addEventListener('beforeunload', onExit);
    window.addEventListener('pageshow', () => {
        pageIsClosing = false;
    });
    document.addEventListener('visibilitychange', () => {
        if (document.hidden) onTabHidden();
        else onTabVisible();
    });
}

function initEngagementTracking() {
    let activityTimer = null;

    function resetActivityTimer() {
        startActiveTimer();
        if (activityTimer) clearTimeout(activityTimer);
        activityTimer = setTimeout(() => stopActiveTimer(), 60000); // стоп через 60 сек неактивности
    }

    // События активности пользователя
    window.addEventListener('mousemove', resetActivityTimer, { passive: true });
    window.addEventListener('keydown', resetActivityTimer, { passive: true });
    window.addEventListener('scroll', resetActivityTimer, { passive: true });
    window.addEventListener('click', resetActivityTimer, { passive: true });

    // События активности вкладки
    window.addEventListener('focus', resetActivityTimer);
    window.addEventListener('blur', () => {
        stopActiveTimer();
        if (activityTimer) clearTimeout(activityTimer);
    });

    // Скрытость вкладки
    document.addEventListener('visibilitychange', () => {
        if (document.hidden) {
            stopActiveTimer();
            if (activityTimer) clearTimeout(activityTimer);
        } else {
            resetActivityTimer();
        }
    });

    resetActivityTimer();
}

export function getSessionId() {
    return sessionId;
}

export function getVisitId() {
    return visitId;
}

export function updateOfferClickId(offerId, targetUrl = null) {
    lastOfferClickId = offerId;
    lastClickTargetUrl = targetUrl;

    // Автоматически отправляем событие редиректа если есть данные клика и URL партнёра.
    if (offerId && targetUrl && lastClickOfferIdOffer && !redirectSent) {
        const redirectMeta = { offer_click_id: offerId };
        if (lastClickOfferVisitId) {
            redirectMeta.offer_visit_id = lastClickOfferVisitId;
        }

        push('offer_redirect', {
            offer_id: lastClickOfferIdOffer,
            offer_click_id: offerId,
            target_url: targetUrl,
        }, redirectMeta);
        redirectSent = true;
        flush(true);
    }
}

export function initTracker() {
    Promise.resolve(apiReady).then(() => {
        if (window.__workOffline === true) {
            return;
        }
        sessionId = getOrCreateSession();
        visitId = typeof window.__visitId !== 'undefined' ? window.__visitId : null;
        if (typeof window.__serverTime === 'number' && window.__serverTime > 0) {
            serverTimeOffset = window.__serverTime - Date.now();
            serverTimeReady = true;
        }

        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', startTracker, { once: true });
        } else {
            startTracker();
        }
    });
}

function startTracker() {
    if (trackerStarted) return;
    trackerStarted = true;
    ensureLocalDevVisitContext();

    pushShowcaseView();

    initScrollTracking();
    initImpressionTracking();
    initClickTracking();
    initEngagementTracking();
    initExitTracking();

    ensureFlushTimer();
}
